import React from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { ShoppingBag, Check, Layers, Sparkles } from "lucide-react";
import { toast } from "sonner";

const WATER_SLUG = "bacteriostatic-water";

function getBundleTier(qualified) {
    if (qualified >= 10) return { pct: 30, next: null };
    if (qualified >= 5) return { pct: 20, next: 10 };
    if (qualified >= 3) return { pct: 10, next: 5 };
    return { pct: 0, next: 3 };
}

export default function ProductCard({ product, index = 0 }) {
    const { addItem, items } = useCart();
    const navigate = useNavigate();
    const [added, setAdded] = React.useState(false);

    const handleAdd = () => {
        addItem(product, 1);
        setAdded(true);
        setTimeout(() => setAdded(false), 1500);
    };

    const handleAddToBundle = () => {
        addItem(product, 1);
        // Compute new qualified count (excluding bacteriostatic water)
        const isWater = product.slug === WATER_SLUG;
        const currentQualified = items.reduce((sum, i) => {
            const w = (i.name || "").toLowerCase().includes("bacteriostatic");
            return w ? sum : sum + i.quantity;
        }, 0);
        const newQualified = currentQualified + (isWater ? 0 : 1);
        const tier = getBundleTier(newQualified);

        if (tier.pct > 0 && tier.next) {
            toast.success(
                `Added! ${tier.pct}% bundle discount unlocked — add ${tier.next - newQualified} more for ${tier.next === 5 ? "20" : "30"}% off`,
                { action: { label: "View bundle", onClick: () => navigate("/bundles") } }
            );
        } else if (tier.pct === 30) {
            toast.success("Added! Max 30% bundle discount unlocked 🎉", {
                action: { label: "View bundle", onClick: () => navigate("/bundles") },
            });
        } else {
            toast.success(
                `Added! Add ${(tier.next || 3) - newQualified} more peptide${(tier.next || 3) - newQualified === 1 ? "" : "s"} to unlock 10% off`,
                { action: { label: "Build bundle", onClick: () => navigate("/bundles") } }
            );
        }
    };

    return (
        <div
            data-testid={`product-card-${product.slug}`}
            className="group relative bg-white rounded-3xl border border-pep-border/60 overflow-hidden lift"
            style={{ animationDelay: `${index * 60}ms` }}
        >
            <div className="relative aspect-square bg-gradient-to-br from-pep-surface to-white overflow-hidden">
                {product.image ? (
                    <img
                        src={product.image}
                        alt={product.name}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                ) : (
                    <div className="w-full h-full flex items-center justify-center text-pep-muted">No image</div>
                )}
                <div className="absolute top-4 left-4 text-[10px] uppercase tracking-widest text-pep-navy/60 bg-white/70 backdrop-blur px-3 py-1 rounded-full">
                    Lab-tested
                </div>
            </div>

            <div className="p-6">
                <div className="flex items-start justify-between gap-3 mb-2">
                    <h3 className="font-heading text-lg font-medium text-pep-navy leading-tight">
                        {product.name}
                    </h3>
                    <span className="font-heading text-lg font-medium text-pep-primary whitespace-nowrap" data-testid={`product-price-${product.slug}`}>
                        ${product.price.toFixed(0)}
                    </span>
                </div>
                <p className="text-pep-muted text-sm mb-5 line-clamp-2">{product.description}</p>

                <div className="space-y-2">
                    <button
                        onClick={handleAdd}
                        disabled={product.in_stock === false}
                        data-testid={`product-add-to-cart-${product.slug}`}
                        className={`w-full rounded-full py-3 text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                            product.in_stock === false
                                ? "bg-pep-surface text-pep-muted cursor-not-allowed opacity-60"
                                : added
                                    ? "bg-pep-navy text-white"
                                    : "bg-pep-surface text-pep-navy hover:bg-pep-primary hover:text-white"
                        }`}
                    >
                        {product.in_stock === false ? (
                            "Out of stock"
                        ) : added ? (
                            <>
                                <Check className="w-4 h-4" /> Added to cart
                            </>
                        ) : (
                            <>
                                <ShoppingBag className="w-4 h-4" /> Add to Cart
                            </>
                        )}
                    </button>
                    {product.in_stock !== false && (
                    <button
                        onClick={handleAddToBundle}
                        data-testid={`product-add-to-bundle-${product.slug}`}
                        className="group/bundle w-full rounded-full py-3 text-sm font-medium border border-pep-primary/30 text-pep-primary bg-white hover:bg-pep-primary hover:text-white transition-all flex items-center justify-center gap-2 relative overflow-hidden"
                    >
                        <span className="flex items-center gap-2 transition-all duration-300 group-hover/bundle:opacity-0 group-hover/bundle:-translate-y-3">
                            <Layers className="w-4 h-4" /> Add to Bundle
                        </span>
                        <span className="absolute inset-0 flex items-center justify-center gap-2 opacity-0 translate-y-3 transition-all duration-300 group-hover/bundle:opacity-100 group-hover/bundle:translate-y-0">
                            <Sparkles className="w-4 h-4" /> Save Up To 30%
                        </span>
                    </button>
                    )}
                </div>
            </div>
        </div>
    );
}
